/* eslint-disable no-console */
import { mapGetters, mapActions, mapState } from 'vuex';
import contentHeader from '@/components/generics/content-header/content-header.vue';
import formCheckbox from '@/components/forms/checkbox/checkbox.vue';
import profile from '@/components/account/profile/profile.vue';
import switchButton from '@/components/generics/switch-button/switch-button.vue';
import alertBox from '@/components/generics/alert-box/alert-box.vue';
import loaderMixin from '@/mixins/loader';
import externalLinksMixin from '@/mixins/externalLinks';
import constants from '@/constants';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import accountDesktopHeader from '@/components/account/account-desktop-header/account-desktop-header.vue';
import userAgentMixin from '@/mixins/userAgent';
import {
  APP_PRICE_DISPLAY,
  APP_MOBILE_ORDERBOOK_DISPLAY,
  APP_MOBILE_LAYOUT,
  APP_COLOR_PALETTE,
  UX_CATEGORY,
} from '@/store/modules/app';
import settingsOption from './settings-option/settings-option.vue';
import settingsOptionSwitch from './settings-option-switch/settings-option-switch.vue';
import checkOption from './check-option/check-option.vue';
import securitySettings from './security-settings/security-settings.vue';

const PROVIDER_FACEBOOK = 'Facebook';
const PROVIDER_TWITTER = 'Twitter';
const PROVIDER_GOOGLE = 'Google';

const optionPush = {
  key: 'AllowPushNotifications',
  info: 'Push Notifications',
};

const sectionNotifications = [
  {
    key: 'OpenOfferMatched',
    info: 'One of my orders has been matched',
  },
  {
    key: 'MarketClosed',
    info: 'One of my markets has closed',
  },
];

if (process.env.SHOW_PRICE_NOTIFICATION_SETTINGS) {
  sectionNotifications.push({
    key: 'ContractPriceChange',
    info: 'One of my contracts changes in price by:',
    withValue: 'ContractPriceChangeAmount',
    validate: x => x >= 5 && x < 100,
    currencyInitial: 5,
  });
}

const sectionGeneral = [
  // {
  //   key: 'ShowBalanceHeader',
  //   info: 'Show my available balances in header',
  // },
];

export const UX_OPTIONS_BUTTON_COLOR_SCHEME = {
  key: UX_CATEGORY.UXSettingBuyColorScheme,
  info: 'Buy Button Color Scheme',
  defaultValue: 1,
  options: [
    {
      key: APP_COLOR_PALETTE.GREEN_RED,
      info: 'Green/Red',
    },
    {
      key: APP_COLOR_PALETTE.BLUE_GREY,
      info: 'Blue/Grey',
    },
  ],
};

export const UX_OPTIONS_PRICE_MOBILE_CONTRACT_LAYOUT = {
  key: UX_CATEGORY.UXSettingMobileContractLayout,
  info: 'Mobile Contract Layout',
  defaultValue: 1,
  options: [
    {
      key: APP_MOBILE_LAYOUT.STANDARD,
      info: 'Tile',
    },
    {
      key: APP_MOBILE_LAYOUT.SIMPLIFIED,
      info: 'Simplified',
    },
    {
      key: APP_MOBILE_LAYOUT.CONDENSED,
      info: 'Condensed',
    },
  ],
};

export const UX_OPTIONS_PRICE_DISPLAY = {
  key: UX_CATEGORY.UXSettingPriceDisplay,
  info: 'Price Display',
  defaultValue: 1,
  options: [
    {
      key: APP_PRICE_DISPLAY.TRADE_PRICES,
      info: 'Trade Prices',
    },
    {
      key: APP_PRICE_DISPLAY.ODDS,
      info: 'Odds',
    },
  ],
};

export const UX_OPTIONS_MOBILE_ORDERBOOK_DISPLAY = {
  key: UX_CATEGORY.UXSettingIsOBCollapsedOnOS,
  info: 'Orderbook Layout (Mobile Only)',
  defaultValue: 1,
  options: [
    {
      key: APP_MOBILE_ORDERBOOK_DISPLAY.COLLAPSED,
      info: 'Collapsed',
    },
    {
      key: APP_MOBILE_ORDERBOOK_DISPLAY.EXPANDED,
      info: 'Expanded',
    },
  ],
};

export const UX_OPTIONS = [
  UX_OPTIONS_BUTTON_COLOR_SCHEME,
  UX_OPTIONS_PRICE_MOBILE_CONTRACT_LAYOUT,
  UX_OPTIONS_PRICE_DISPLAY,
];

export const UX_OPTIONS_WITHOUT_MOBILE_ORDERBOOK_SETTING = [
  ...UX_OPTIONS.filter(option => option.key !== UX_CATEGORY.UXSettingIsOBCollapsedOnOS),
];


export default {
  components: {
    accountDesktopHeader,
    alertBox,
    contentHeader,
    formCheckbox,
    profile,
    switchButton,
    settingsOption,
    checkOption,
    securitySettings,
    settingsOptionSwitch,
  },
  mixins: [
    externalLinksMixin,
    loaderMixin,
    userAgentMixin,
  ],
  data() {
    return {
      manageInfoLoaded: false,
      correspondence: false,
      logins: [],
      hasLocalPassword: false,
      sectionGeneral,
      sectionNotifications,
      optionPush,
      sectionPushEnabled: false,
      categories: {
        privacy: 'privacy',
        general: 'display',
        push: 'PushNotifications',
        inApp: 'InAppNotifications',
        ux: 'ux',
      },
      sectionUX: UX_OPTIONS,
    };
  },
  metaInfo() {
    return {
      title: 'Settings',
    };
  },
  computed: {
    ...mapGetters('app', ['isLayoutMobile', 'leaderboardsEnabled']),
    ...mapGetters('profile', ['settingsReady']),
    ...mapState('profile', ['settings']),
    sectionPrivacy() {
      const results = [
        {
          key: 'ShowTradeHistory',
          info: "Show my trade activity in 'Currently Predicting'",
        },
      ];
      if (this.leaderboardsEnabled) {
        results.push({
          key: 'AllowLeaderboard',
          info: 'Show my ranking in the Leaderboards',
        });
      }
      return results;
    },
    gridRowModifier() {
      return {
        row: this.isLayoutMobile,
        'row-x': !this.isLayoutMobile,
      };
    },
    isFacebookDisabled() {
      return !this.hasLocalPassword;
    },
    isTwitterDisabled() {
      return !this.hasLocalPassword;
    },
    isGoogleDisabled() {
      return !this.hasLocalPassword;
    },
    hasFacebook() {
      return this.checkLogin(PROVIDER_FACEBOOK);
    },
    hasTwitter() {
      return this.checkLogin(PROVIDER_TWITTER);
    },
    hasGoogle() {
      return this.checkLogin(PROVIDER_GOOGLE);
    },
    hasGeneral() {
      if (this.settings.display && typeof this.settings.display.ShowBalanceHeader !== 'undefined') {
        return true;
      }
      return false;
    },
    hasPrivacy() {
      if (
        this.settings.privacy
        && typeof this.settings.privacy.ShowTradeHistory !== 'undefined'
        && typeof this.settings.privacy.AllowLeaderboard !== 'undefined'
      ) {
        return true;
      }
      return false;
    },
    hasUX() {
      return this.settings.ux
      && typeof this.settings.ux.UXSettingBuyColorScheme !== 'undefined'
      && typeof this.settings.ux.UXSettingMobileContractLayout !== 'undefined'
      && typeof this.settings.ux.UXSettingPriceDisplay !== 'undefined';
    },
    hasPush() {
      if (this.settings.PushNotifications && typeof this.settings.PushNotifications.AllowPushNotifications !== 'undefined') {
        this.sectionPushEnabled = this.settings.PushNotifications.AllowPushNotifications;
        return true;
      }
      return false;
    },
    isPushDisabled() {
      return !this.sectionPushEnabled;
    },
    haveSettingsLoaded() {
      return this.hasGeneral || this.hasPrivacy || this.hasPush;
    },
  },
  created() {
    this.loaderAdd(document.body);
    this.clearSettings();
    this.getAllSettings()
      .then(this.checkManageInfo)
      .then(() => {
        this.manageInfoLoaded = true;
      })
      .then(() => this.getExternalLogins({
        type: constants.SOCIAL_LOGIN_ADD,
      }))
      .then((response) => {
        this.links = response.data;
      })
      .then(() => {
        this.loaderRemove(document.body);
      });
  },
  methods: {
    ...mapActions('notifications', ['setErrorSideNotification', 'setSuccessSideNotification']),
    ...mapActions('user', ['getManageInfo', 'removeExternal', 'logout']),
    ...mapActions('popup', ['setCurrentSmallPopup']),
    ...mapActions('profile', ['getAllSettings', 'clearSettings']),
    checkProvider(x, target) {
      return x.loginProvider.toLowerCase() === target.toLowerCase();
    },
    checkLogin(target) {
      return this.logins &&
        this.logins.length > 0 &&
        (this.logins.filter(x => this.checkProvider(x, target)).length === 1);
    },
    getProviderKey(target) {
      if (this.logins && this.logins.length > 0) {
        const login = this.logins.filter(x => this.checkProvider(x, target))[0];
        return (login) ? login.providerKey : {};
      }
      return {};
    },
    checkManageInfo() {
      return this.getManageInfo()
        .then((data) => {
          this.hasLocalPassword = data.hasLocalPassword;
          this.logins = data.logins;
        });
    },
    onToggleProvider(provider, value) {
      if (value) {
        if (!this.isWrapperApp) {
          this.loaderAdd(document.body);
        }
        try {
          if (provider === PROVIDER_FACEBOOK) this.facebookLinkClick();
          if (provider === PROVIDER_TWITTER) this.twitterLinkClick();
          if (provider === PROVIDER_GOOGLE) this.googleLinkClick();
        } catch (err) {
          this.loaderRemove(document.body);
          console.error(err);
        }
      } else {
        const payload = {
          loginProvider: provider,
          providerKey: this.getProviderKey(provider),
        };
        this.removeExternal(payload)
          .then(() => this.setSuccessSideNotification('Settings updated successfully.'))
          .then(this.getAllSettings)
          .then(this.checkManageInfo)
          .catch(() => this.setErrorSideNotification('Error has occured.'));
      }
    },
    onToggleSetting(item) {
      if (item.key === 'AllowLeaderboard') {
        this.setCurrentSmallPopup(MODALS.SMALL_LEADERBOARDS_UPDATE_INFO);
      }
    },
    onSetPassword() {
      this.$router.push({ name: 'password-set' });
    },
    doError(e) {
      this.setErrorSideNotification(e);
    },
    togglePush(e) {
      this.sectionPushEnabled = e;
    },

  },
};



// WEBPACK FOOTER //
// ./src/components/account/settings/settings.js